import { createFileRoute, Link } from "@tanstack/react-router";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { useSubmissions, useAuth } from "@/projects/lib/useStore";
import {
  FileVideo, Clock, Trophy, AlertTriangle, TrendingUp, TrendingDown,
  CheckCircle2, Activity, ArrowRight,
} from "lucide-react";
import { StatusBadge } from "@/projects/components/status-badge";
import { cn } from "@/projects/lib/utils";
import { useMemo } from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";

export const Route = createFileRoute("/teacher/")({
  head: () => ({ meta: [{ title: "Teacher Dashboard — રાજનગર જ્ઞાનોત્સવ 2.0" }] }),
  component: TeacherDashboard,
});

const C = {
  royal: "oklch(0.42 0.15 265)",
  gold: "oklch(0.78 0.14 85)",
  saffron: "oklch(0.72 0.18 55)",
  success: "oklch(0.62 0.16 145)",
  danger: "oklch(0.58 0.22 27)",
};

function TeacherDashboard() {
  const auth = useAuth();
  const all = useSubmissions();
  const mine = useMemo(() => all.filter((s) => s.assignedTeacherId === auth?.id), [all, auth]);

  const pending = mine.filter((s) => s.status === "Assigned for Verification").length;
  const reward = mine.filter((s) => s.status === "Ready for Reward").length;
  const notProper = mine.filter((s) => s.status === "Not Proper").length;
  const verified = reward + notProper;
  const completion = mine.length ? Math.round((verified / mine.length) * 100) : 0;
  const approvalRate = verified ? Math.round((reward / verified) * 100) : 0;

  // Daily activity for last 14 days
  const byDate = useMemo(() => {
    const map = new Map<string, { assigned: number; verified: number }>();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000);
      const k = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
      map.set(k, { assigned: 0, verified: 0 });
    }
    mine.forEach((s) => {
      if (s.assignedAt) {
        const k = new Date(s.assignedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
        const cur = map.get(k);
        if (cur) cur.assigned += 1;
      }
      if (s.verifiedAt) {
        const k = new Date(s.verifiedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
        const cur = map.get(k);
        if (cur) cur.verified += 1;
      }
    });
    return Array.from(map.entries()).map(([date, v]) => ({ date, ...v }));
  }, [mine]);

  const outcome = [
    { name: "Ready for Reward", value: reward, color: C.success },
    { name: "Not Proper", value: notProper, color: C.danger },
    { name: "Pending", value: pending, color: C.saffron },
  ];

  const gauge = [{ name: "rate", value: completion, fill: C.gold }];

  const kpis = [
    { label: "Total Assigned", value: mine.length, delta: 6, up: true, icon: FileVideo, tone: "royal" as const },
    { label: "Pending Verification", value: pending, delta: 3, up: false, icon: Clock, tone: "saffron" as const },
    { label: "Ready for Reward", value: reward, delta: 12, up: true, icon: Trophy, tone: "success" as const },
    { label: "Not Proper", value: notProper, delta: 1, up: false, icon: AlertTriangle, tone: "danger" as const },
  ];

  const recent = [...mine]
    .sort((a, b) => new Date(b.assignedAt ?? b.submittedAt).getTime() - new Date(a.assignedAt ?? a.submittedAt).getTime())
    .slice(0, 6);

  return (
    <DashboardLayout role="teacher" title={`Welcome, ${auth?.name ?? "Teacher"}`} breadcrumb={["Home", "Dashboard"]}>
      {/* Welcome banner */}
      <div className="mb-4 overflow-hidden rounded-xl border bg-gradient-to-r from-royal via-royal to-royal/80 p-5 text-white shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-gold">Verification Console</div>
            <div className="mt-1 text-xl font-bold sm:text-2xl">Good to see you, {auth?.name?.split(" ")[0] ?? "Teacher"} 👋</div>
            <div className="mt-1 text-sm text-white/80">You have <span className="font-semibold text-gold">{pending}</span> videos awaiting review.</div>
          </div>
          <Link
            to="/teacher/submissions"
            className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-primary hover:bg-gold/90"
          >
            Review Queue <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="relative overflow-hidden rounded-xl border bg-card p-4 shadow-soft">
            <div className={cn(
              "absolute inset-x-0 top-0 h-1",
              k.tone === "royal" && "bg-royal",
              k.tone === "saffron" && "bg-saffron",
              k.tone === "success" && "bg-success",
              k.tone === "danger" && "bg-destructive",
            )} />
            <div className="flex items-start justify-between">
              <div className={cn(
                "grid h-9 w-9 place-items-center rounded-lg",
                k.tone === "royal" && "bg-royal-soft text-royal",
                k.tone === "saffron" && "bg-saffron-soft text-saffron",
                k.tone === "success" && "bg-success/10 text-success",
                k.tone === "danger" && "bg-destructive/10 text-destructive",
              )}>
                <k.icon className="h-4.5 w-4.5" />
              </div>
              <div className={cn(
                "flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                k.up ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
              )}>
                {k.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {k.delta}%
              </div>
            </div>
            <div className="mt-3 text-2xl font-bold">{k.value}</div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="mt-4 grid gap-4 lg:grid-cols-6">
        <Panel title="Verification Activity" subtitle="Assigned vs verified · last 14 days" className="lg:col-span-4">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={byDate} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="t-a" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.royal} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={C.royal} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="t-v" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.success} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={C.success} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="date" fontSize={10} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="assigned" name="Assigned" stroke={C.royal} strokeWidth={2} fill="url(#t-a)" />
              <Area type="monotone" dataKey="verified" name="Verified" stroke={C.success} strokeWidth={2} fill="url(#t-v)" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Completion" subtitle="Verified vs assigned" className="lg:col-span-2">
          <div className="relative">
            <ResponsiveContainer width="100%" height={220}>
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={gauge} startAngle={220} endAngle={-40}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar dataKey="value" cornerRadius={12} background={{ fill: "oklch(0.94 0.02 265)" }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-4xl font-extrabold text-royal">{completion}%</div>
              <div className="text-xs text-muted-foreground">{verified} of {mine.length}</div>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-xs">
            <span className="text-muted-foreground">Approval rate</span>
            <span className="font-semibold text-success">{approvalRate}%</span>
          </div>
        </Panel>

        <Panel title="Outcome Split" subtitle="Where your reviews land" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={outcome} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3} stroke="none">
                {outcome.map((s, i) => <Cell key={i} fill={s.color} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 text-xs">
            {outcome.map((o) => (
              <div key={o.name} className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: o.color }} />
                <span className="text-muted-foreground">{o.name}</span>
                <span className="ml-auto font-semibold">{o.value}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Daily Throughput" subtitle="Videos verified per day" className="lg:col-span-4">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byDate} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="date" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="verified" name="Verified" fill={C.gold} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Recent assignments */}
      <Panel title="Recent Assignments" subtitle="Latest videos assigned to you" className="mt-4">
        {mine.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">No submissions are currently assigned to you.</div>
        ) : (
          <div className="divide-y">
            {recent.map((s) => (
              <Link
                key={s.id}
                to="/teacher/verify/$id"
                params={{ id: s.id }}
                className="flex items-center gap-3 py-2.5 hover:bg-muted/40"
              >
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-royal-soft text-royal">
                  <FileVideo className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{s.videoTitle}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.studentName} · {s.studentId} · <span className="font-mono">{s.id}</span>
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </Link>
            ))}
          </div>
        )}
        <div className="mt-3 flex justify-end">
          <Link to="/teacher/submissions" className="inline-flex items-center gap-1 text-xs font-medium text-royal hover:underline">
            View all assignments <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </Panel>
    </DashboardLayout>
  );
}

function Panel({
  title, subtitle, children, className,
}: { title: string; subtitle?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border bg-card p-4 shadow-soft sm:p-5", className)}>
      <div className="mb-3">
        <div className="text-sm font-semibold">{title}</div>
        {subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}
